import { useCallback, useEffect, useMemo, useState } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import {
	distributeVisibleColumnWidths,
	getBrowserAvailableWidth,
} from "~/shared/utils/grid-column-utils.ts";

export const useAutoResizeColumns = (columns: BoardColumn[]) => {
	const [wrapperElement, setWrapperElement] = useState<HTMLDivElement | null>(
		null,
	);

	const wrapperRef = useCallback((node: HTMLDivElement | null) => {
		setWrapperElement(node);
	}, []);

	const [containerWidth, setContainerWidth] = useState<number | null>(null);

	useEffect(() => {
		if (!wrapperElement) {
			setContainerWidth(getBrowserAvailableWidth());
			return;
		}

		const ro = new ResizeObserver((entries) => {
			for (const entry of entries) {
				setContainerWidth(entry.contentRect.width);
			}
		});

		ro.observe(wrapperElement);
		return () => ro.disconnect();
	}, [wrapperElement]);

	const distributedColumns = useMemo(() => {
		const width = containerWidth ?? getBrowserAvailableWidth();
		// Subtract buffer for vertical scrollbar to prevent horizontal scrolling
		const scrollbarBuffer = 18;
		return distributeVisibleColumnWidths(columns, width - scrollbarBuffer);
	}, [columns, containerWidth]);

	return {
		wrapperRef,
		distributedColumns,
	};
};
